import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Marker, InfoWindow } from 'react-google-maps';
import StationInfoWindow from '../StationInfoWindow';
import createMarkerIcon from '../../utils/createMarkerIcon';
import getMarkerColor from '../../utils/getMarkerColor';
import EmergencyStatusPropType from '../../utils/prop-types/emergency-status';
import { noop } from '../../../lib/utils';
import usePrevious from '../../../lib/hooks/usePrevious';

export default function StationMarker({
  onClick,
  onShow,
  onHide,
  open,
  station,
}) {
  const [isShowingStationInfo, setIsShowingStationInfo] = useState(open);

  const wasShowingStationInfo = usePrevious(open);

  const handleClick = useCallback((e) => {
    onClick(e);
    setIsShowingStationInfo(true);
  }, [onClick]);

  const handleClose = useCallback(() => {
    setIsShowingStationInfo(false);
  }, []);

  useEffect(() => {
    if (isShowingStationInfo !== open) {
      if (!wasShowingStationInfo) {
        onShow();
        setIsShowingStationInfo(true);
      } else {
        onHide();
        setIsShowingStationInfo(false);
      }
    }
  }, [isShowingStationInfo, wasShowingStationInfo, open, onShow, onHide]);

  const markerColor = getMarkerColor(station.emergency_status, station.fuels);

  return (
    <Marker
      optimize={false}
      icon={createMarkerIcon(markerColor)}
      position={{ lat: station.latitude, lng: station.longitude }}
      onClick={handleClick}
    >
      {isShowingStationInfo && (
        <InfoWindow onCloseClick={handleClose}>
          <StationInfoWindow
            id={station.id}
            address={station.address}
            name={station.name}
            phone={station.phone}
            fuels={station.fuels}
            emergencyStatus={station.emergency_status}
          />
        </InfoWindow>
      )}
    </Marker>
  );
}

StationMarker.propTypes = {
  onClick: PropTypes.func,
  onShow: PropTypes.func,
  onHide: PropTypes.func,
  open: PropTypes.bool,
  station: PropTypes.shape({
    id: PropTypes.number.isRequired,
    address: PropTypes.shape({}),
    name: PropTypes.string,
    phone: PropTypes.string,
    emergency_status: EmergencyStatusPropType,
  }).isRequired,
};

StationMarker.defaultProps = {
  onClick: noop,
  onShow: noop,
  onHide: noop,
  open: false,
};
